(function () {
    function getBundlePropertyLabels(config) {
      const settings = window.themeVariables?.settings || {};
      return {
        addons: config?.addonsPropertyLabel || settings.addonsPropertyLabel || "_Addon",
        main: config?.mainProductPropertyLabel || settings.mainProductPropertyLabel || "_Main",
      };
    }
  
    function getLineProperty(item, key) {
      if (!item?.properties) return null;
      const value = item.properties[key];
      return value == null || value === "" ? null : String(value);
    }
  
    function getBundleTimestamp(item, labels) {
      return getLineProperty(item, labels.main) || getLineProperty(item, labels.addons);
    }
  
    function isBundleMain(item, labels) {
      return Boolean(getLineProperty(item, labels.main));
    }
  
    function isBundleAddon(item, labels) {
      return Boolean(getLineProperty(item, labels.addons));
    }
  
    function formatMoney(cents, format) {
      if (typeof window.formatMoney === "function") {
        return window.formatMoney(cents, format);
      }
      if (typeof cents === "string") cents = cents.replace(".", "");
      const moneyFormat =
        format ||
        (window.themeVariables?.settings?.currencyCodeEnabled
          ? window.themeVariables.settings.moneyWithCurrencyFormat
          : window.themeVariables?.settings?.moneyFormat) ||
        "{{amount}}";
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = moneyFormat.match(placeholderRegex);
      if (!match) return (cents / 100).toFixed(2);
      const value = (cents / 100).toFixed(match[1] === "amount_no_decimals" ? 0 : 2);
      return moneyFormat.replace(placeholderRegex, value);
    }
  
    function parseConfig(element) {
      const script = element.querySelector("[data-bundle-config]");
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.error("Bundle builder: invalid config JSON", e);
        return null;
      }
    }
  
    function buildProductLookup(config) {
      const map = new Map();
      const addProduct = (product, categoryId = "", categoryTitle = "") => {
        map.set(String(product.variantId), {
          ...product,
          categoryId,
          category: categoryTitle || product.category || "",
        });
      };
  
      (config.preselectedProducts || []).forEach((product) => {
        addProduct(product, "preselected", product.category || "");
      });
  
      (config.categories || []).forEach((category) => {
        (category.products || []).forEach((product) => {
          addProduct(product, category.id, category.title);
        });
      });
  
      return map;
    }
  
    function sortTiers(tiers) {
      return [...(tiers || [])].sort((a, b) => Number(a.max) - Number(b.max));
    }
  
    function sortFreeGifts(gifts) {
      return [...(gifts || [])].sort((a, b) => Number(a.minQuantity) - Number(b.minQuantity));
    }
  
    function prepareBundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections },
        })
      );
      return sections;
    }
  
    const BundleCartSync = {
      isSyncing: false,
      cartCache: null,
  
      init() {
        if (this._initialized) return;
        this._initialized = true;
  
        document.addEventListener("change", this._onQuantityChange.bind(this), true);
        document.addEventListener("click", this._onCartLinkClick.bind(this), true);
        document.addEventListener("cart:change", this._onCartChange.bind(this));
      },
  
      async fetchCart(force = false) {
        if (!force && this.cartCache) return this.cartCache;
        const response = await fetch(`${Shopify.routes.root}cart.js`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        if (!response.ok) throw new Error("Failed to load cart");
        this.cartCache = await response.json();
        return this.cartCache;
      },
  
      get labels() {
        return getBundlePropertyLabels();
      },
  
      getChildren(cart, bundleTimestamp) {
        const labels = this.labels;
        return cart.items.filter(
          (item) =>
            isBundleAddon(item, labels) && getLineProperty(item, labels.addons) === bundleTimestamp
        );
      },
  
      getParent(cart, bundleTimestamp) {
        const labels = this.labels;
        return cart.items.find(
          (item) => isBundleMain(item, labels) && getLineProperty(item, labels.main) === bundleTimestamp
        );
      },
  
      getBundleLines(cart, bundleTimestamp) {
        const labels = this.labels;
        return cart.items.filter((item) => getBundleTimestamp(item, labels) === bundleTimestamp);
      },
  
      buildRemoveBundleUpdates(cart, bundleTimestamp) {
        const updates = {};
        this.getBundleLines(cart, bundleTimestamp).forEach((item) => {
          updates[item.key] = 0;
        });
        return Object.keys(updates).length ? updates : null;
      },
  
      async removeBundle(cart, bundleTimestamp) {
        const updates = this.buildRemoveBundleUpdates(cart, bundleTimestamp);
        if (!updates) return null;
        return this.applyUpdates(updates);
      },
  
      getQtyPerParent(child, parent) {
        if (parent?.quantity > 0) return child.quantity / parent.quantity;
        return child.quantity || 1;
      },
  
      buildChildQuantity(qtyPerParent, parentQuantity) {
        if (parentQuantity <= 0) return 0;
        return Math.max(0, Math.round(qtyPerParent * parentQuantity));
      },
  
      buildUpdatesForParent(cart, parentLine, targetParentQuantity) {
        const bundleTimestamp = getBundleTimestamp(parentLine, this.labels);
        if (!bundleTimestamp) return null;
  
        const updates = { [parentLine.key]: targetParentQuantity };
        const children = this.getChildren(cart, bundleTimestamp);
  
        children.forEach((child) => {
          const qtyPerParent = this.getQtyPerParent(child, parentLine);
          updates[child.key] = this.buildChildQuantity(qtyPerParent, targetParentQuantity);
        });
  
        return updates;
      },
  
      async applyUpdates(updates) {
        this.isSyncing = true;
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  
        const sectionsToBundle = prepareBundledSections();
  
        try {
          const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            credentials: "same-origin",
            body: JSON.stringify({
              updates,
              sections: sectionsToBundle.join(","),
            }),
          });
  
          const cartContent = await response.json();
          if (!response.ok) {
            throw new Error(cartContent.description || cartContent.message || "Cart update failed");
          }
  
          this.cartCache = cartContent;
  
          document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:change", {
              bubbles: true,
              detail: {
                baseEvent: "bundle:cart-sync",
                cart: cartContent,
              },
            })
          );
  
          return cartContent;
        } finally {
          this.isSyncing = false;
        }
      },
  
      async syncParentLine(parentLine, targetQuantity) {
        const cart = await this.fetchCart(true);
        const bundleTimestamp = getBundleTimestamp(parentLine, this.labels);
  
        if (!bundleTimestamp || targetQuantity <= 0) {
          if (!bundleTimestamp) return null;
          return this.removeBundle(cart, bundleTimestamp);
        }
  
        const updates = this.buildUpdatesForParent(cart, parentLine, targetQuantity);
        if (!updates) return null;
        return this.applyUpdates(updates);
      },
  
      async cleanupIncompleteBundles(cart) {
        const labels = this.labels;
        const timestamps = new Set();
  
        cart.items.forEach((item) => {
          const bundleTimestamp = getBundleTimestamp(item, labels);
          if (bundleTimestamp) timestamps.add(bundleTimestamp);
        });
  
        const updates = {};
        timestamps.forEach((bundleTimestamp) => {
          if (!this.getParent(cart, bundleTimestamp)) {
            this.getBundleLines(cart, bundleTimestamp).forEach((item) => {
              updates[item.key] = 0;
            });
          }
        });
  
        if (!Object.keys(updates).length) return cart;
        return this.applyUpdates(updates);
      },
  
      async handleBundleLineChange(cart, lineKey, targetQuantity) {
        const labels = this.labels;
        const line = cart.items.find((item) => item.key === lineKey);
        if (!line) return false;
  
        const bundleTimestamp = getBundleTimestamp(line, labels);
        if (!bundleTimestamp) return false;
  
        if (targetQuantity <= 0) {
          await this.removeBundle(cart, bundleTimestamp);
          return true;
        }
  
        if (isBundleMain(line, labels)) {
          await this.syncParentLine(line, targetQuantity);
          return true;
        }
  
        return false;
      },
  
      async _onQuantityChange(event) {
        if (this.isSyncing) return;
  
        const input = event.target;
        if (!input?.matches?.("[data-line-key]")) return;
  
        const lineKey = input.getAttribute("data-line-key");
        const targetQuantity = parseInt(input.value, 10);
        if (!Number.isFinite(targetQuantity)) return;
  
        let cart;
        try {
          cart = await this.fetchCart(true);
        } catch {
          return;
        }
  
        const line = cart.items.find((item) => item.key === lineKey);
        if (!line || !getBundleTimestamp(line, this.labels)) return;
  
        try {
          const handled = await this.handleBundleLineChange(cart, lineKey, targetQuantity);
          if (!handled) return;
        } catch (error) {
          console.error("Bundle cart sync:", error);
          return;
        }
  
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      },
  
      async _onCartLinkClick(event) {
        if (this.isSyncing) return;
  
        const link = event.target.closest('a[href*="/cart/change"], a.line-item-remove');
        if (!link) return;
  
        let lineKey;
        let targetQuantity;
  
        if (link.href && link.href.includes("/cart/change")) {
          const url = new URL(link.href, window.location.origin);
          lineKey = url.searchParams.get("id");
          targetQuantity = parseInt(url.searchParams.get("quantity"), 10);
        } else if (link.href) {
          const url = new URL(link.href, window.location.origin);
          lineKey = url.searchParams.get("id");
          targetQuantity = 0;
        }
  
        if (!lineKey || !Number.isFinite(targetQuantity)) return;
  
        let cart;
        try {
          cart = await this.fetchCart(true);
        } catch {
          return;
        }
  
        const line = cart.items.find((item) => item.key === lineKey);
        if (!line || !getBundleTimestamp(line, this.labels)) return;
  
        try {
          const handled = await this.handleBundleLineChange(cart, lineKey, targetQuantity);
          if (!handled) return;
        } catch (error) {
          console.error("Bundle cart sync:", error);
          return;
        }
  
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      },
  
      async _onCartChange(event) {
        if (this.isSyncing) return;
        if (event.detail?.baseEvent === "bundle:cart-sync") return;
  
        const cart = event.detail?.cart;
        if (!cart?.items) return;
  
        this.cartCache = cart;
  
        const labels = this.labels;
        const needsCleanup = cart.items.some((item) => {
          if (!isBundleAddon(item, labels)) return false;
          const bundleTimestamp = getLineProperty(item, labels.addons);
          return bundleTimestamp && !this.getParent(cart, bundleTimestamp);
        });
  
        if (!needsCleanup) return;
  
        try {
          await this.cleanupIncompleteBundles(cart);
        } catch (error) {
          console.error("Bundle cart cleanup:", error);
        }
      },
    };
  
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.config = null;
        this.productLookup = new Map();
        this.selection = new Map();
        this.popupDraft = new Map();
        this.preselectedIds = new Set();
        this.mainVariantPrice = 0;
        this._onFormSubmit = this._onFormSubmit.bind(this);
        this._onVariantChange = this._onVariantChange.bind(this);
        this._onDocumentKeydown = this._onDocumentKeydown.bind(this);
      }
  
      connectedCallback() {
        this.config = parseConfig(this);
        if (!this.config) {
          console.error("Bundle builder: missing or invalid config JSON");
          return;
        }
  
        this.productLookup = buildProductLookup(this.config);
        this.tiers = sortTiers(this.config.discountTiers);
        this.freeGifts = sortFreeGifts(this.config.freeGifts);
        this.preselectedIds = new Set();
        this._initPreselected();
  
        this._cacheElements();
        this._buildPopupTabs();
        this._bindEvents();
        this._renderAll();
        this._bindForm();
      }
  
      disconnectedCallback() {
        this._formBindObserver?.disconnect();
        this._formBindObserver = null;
        this.form?.removeEventListener("submit", this._onFormSubmit, true);
        this.form?.removeEventListener("variant:change", this._onVariantChange);
        if (this._qtyChangeHandler && this.form) {
          const qtyInput = this.form.querySelector('[name="quantity"]');
          qtyInput?.removeEventListener("change", this._qtyChangeHandler);
          qtyInput?.removeEventListener("input", this._qtyChangeHandler);
        }
        document.removeEventListener("keydown", this._onDocumentKeydown);
        this._restorePopupToHost();
        this._formBound = false;
      }
  
      _bindForm() {
        if (this._formBound) return;
  
        const formId = this.getAttribute("form");
        this.form = document.forms[formId];
  
        if (!this.form) {
          if (!this._formBindObserver) {
            this._formBindObserver = new MutationObserver(() => {
              this.form = document.forms[formId];
              if (this.form) {
                this._formBindObserver.disconnect();
                this._formBindObserver = null;
                this._attachToForm();
              }
            });
            this._formBindObserver.observe(document.documentElement, {
              childList: true,
              subtree: true,
            });
          }
          return;
        }
  
        this._attachToForm();
      }
  
      _attachToForm() {
        if (this._formBound || !this.form) return;
        this._formBound = true;
  
        this._readMainVariantPrice();
        this.form.addEventListener("submit", this._onFormSubmit, true);
        this.form.addEventListener("variant:change", this._onVariantChange);
  
        this._qtyChangeHandler = () => this._scheduleDisplayedPriceUpdate();
        const qtyInput = this.form.querySelector('[name="quantity"]');
        qtyInput?.addEventListener("change", this._qtyChangeHandler);
        qtyInput?.addEventListener("input", this._qtyChangeHandler);
      }
  
      _cacheElements() {
        this.selectionGrid = this.querySelector("[data-bundle-selection-grid]");
        this.popup = this.querySelector("[data-bundle-popup]");
        this.popupOverlay = this.querySelector("[data-bundle-popup-overlay]");
        this.popupDialog = this.querySelector(".bundle-popup__dialog");
        this.popupClose = this.querySelector("[data-bundle-popup-close]");
        this.popupTabs = this.querySelector("[data-bundle-popup-tabs]");
        this.popupPanels = this.querySelectorAll("[data-bundle-popup-panel]");
        this.popupConfirm = this.querySelector("[data-bundle-popup-confirm]");
        this.popupConfirmHeader = this.querySelector("[data-bundle-popup-confirm-header]");
      }
  
      _bindEvents() {
        this.popupConfirm?.addEventListener("click", () => this._confirmPopup());
        this.popupConfirmHeader?.addEventListener("click", () => this._confirmPopup());
        this.popupClose?.addEventListener("click", () => this._closePopup());
        this.popupOverlay?.addEventListener("click", () => this._closePopup());
        document.addEventListener("keydown", this._onDocumentKeydown);
  
        this.popup?.querySelectorAll("[data-bundle-product-card]").forEach((card) => {
          this._bindProductCard(card);
        });
      }
  
      _onDocumentKeydown(event) {
        if (event.key !== "Escape" || !this.popup?.classList.contains("is-open")) return;
        this._closePopup();
      }
  
      _movePopupToBody() {
        if (!this.popup || this.popup.parentElement === document.body) return;
        this._popupRestoreParent = this.popup.parentElement;
        this._popupRestoreNextSibling = this.popup.nextSibling;
        document.body.appendChild(this.popup);
      }
  
      _restorePopupToHost() {
        if (!this.popup || this.popup.parentElement !== document.body) return;
        const parent = this._popupRestoreParent;
        if (parent && parent.isConnected) {
          if (this._popupRestoreNextSibling && this._popupRestoreNextSibling.parentNode === parent) {
            parent.insertBefore(this.popup, this._popupRestoreNextSibling);
          } else {
            parent.appendChild(this.popup);
          }
        } else {
          this.appendChild(this.popup);
        }
        this._popupRestoreParent = null;
        this._popupRestoreNextSibling = null;
      }
  
      _openPopup() {
        if (!this.popup) return;
        this._movePopupToBody();
        this._syncPopupFromSelection();
        this.popup.hidden = false;
        this.popup.setAttribute("aria-hidden", "false");
        this.popup.classList.add("is-open");
        document.documentElement.classList.add("bundle-popup-open");
        if (this.shouldShowDiscountProgress()) {
          this._updateDiscountTracker();
        }
        this.popupDialog?.focus();
      }
  
      _closePopup() {
        if (!this.popup) return;
        this.popup.classList.remove("is-open");
        this.popup.hidden = true;
        this.popup.setAttribute("aria-hidden", "true");
        document.documentElement.classList.remove("bundle-popup-open");
        this._restorePopupToHost();
      }
  
      _syncBundleQtyInput(input, quantity) {
        if (!input) return;
        const value = String(Math.max(0, quantity));
        input.value = value;
        input.style.setProperty("--quantity-selector-character-count", `${value.length}ch`);
      }
  
      _bindProductCard(card) {
        const variantId = String(card.dataset.variantId);
        const minus = card.querySelector("[data-bundle-qty-minus]");
        const plus = card.querySelector("[data-bundle-qty-plus]");
        const input = card.querySelector("[data-bundle-qty-input]");
  
        const adjustQuantity = (delta, event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          const current = this.popupDraft.get(variantId)?.quantity || 0;
          this._setQuantity(variantId, current + delta, true);
        };
  
        minus?.addEventListener("click", (event) => adjustQuantity(-1, event), true);
        plus?.addEventListener("click", (event) => adjustQuantity(1, event), true);
  
        input?.addEventListener("change", () => {
          const value = parseInt(input.value, 10) || 0;
          this._setQuantity(variantId, value, true);
        });
  
        input?.addEventListener("input", () => {
          input.style.setProperty("--quantity-selector-character-count", `${input.value.length}ch`);
        });
      }
  
      _buildPopupTabs() {
        if (!this.popupTabs) return;
        this.popupTabs.innerHTML = "";
        const categories = this.config.categories || [];
  
        categories.forEach((category, index) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = `bundle-popup__tab${index === 0 ? " is-active" : ""}`;
          button.textContent = category.title;
          button.dataset.categoryId = category.id;
          button.addEventListener("click", () => this._activateCategory(category.id));
          this.popupTabs.appendChild(button);
        });
  
        if (categories[0]) this._activateCategory(categories[0].id);
      }
  
      _activateCategory(categoryId) {
        this.popupTabs?.querySelectorAll(".bundle-popup__tab").forEach((tab) => {
          tab.classList.toggle("is-active", tab.dataset.categoryId === categoryId);
        });
        this.popupPanels?.forEach((panel) => {
          panel.hidden = panel.dataset.categoryId !== categoryId;
        });
      }
  
      get isPreselectedMode() {
        const products = this.config?.preselectedProducts || [];
        return Boolean(
          this.config?.preselectedEnabled &&
          (products.length > 0 || this.preselectedIds.size > 0)
        );
      }
  
      _initPreselected() {
        if (!this.config?.preselectedEnabled) return;
  
        const products = this.config.preselectedProducts || [];
        if (!products.length) {
          console.warn(
            "Bundle builder: preselected mode is enabled but no preselected products were loaded. Check section settings."
          );
          return;
        }
  
        products.forEach((product) => {
          if (!product?.variantId || !product.available) return;
  
          const variantId = String(product.variantId);
          this.preselectedIds.add(variantId);
          this.selection.set(variantId, {
            ...product,
            quantity: 1,
            isPreselected: true,
          });
        });
      }
  
      isPreselectedItem(item) {
        return Boolean(item?.isPreselected);
      }
  
      isPreselectedVariant(variantId, map = this.selection) {
        const item = map.get(String(variantId));
        return this.isPreselectedItem(item);
      }
  
      get canRemovePreselected() {
        return Boolean(this.config.preselectedAllowRemove);
      }
  
      getActivePreselectedCount(map = this.selection) {
        let count = 0;
        map.forEach((item) => {
          if (item.quantity > 0 && item.isPreselected) count += 1;
        });
        return count;
      }
  
      getCustomerSelectionQty(map = this.selection) {
        let total = 0;
        map.forEach((item) => {
          if (item.quantity > 0 && !item.isPreselected) {
            total += item.quantity;
          }
        });
        return total;
      }
  
      getMaxCustomerSlots() {
        if (!this.isPreselectedMode) return this.config.maxProducts;
        return Math.max(0, this.config.maxProducts - this.getActivePreselectedCount());
      }
  
      getCustomerUniqueCount(map = this.selection) {
        let count = 0;
        map.forEach((item) => {
          if (item.quantity > 0 && !item.isPreselected) count += 1;
        });
        return count;
      }
  
      getTotalQuantity(map = this.selection) {
        let total = 0;
        map.forEach((item) => {
          total += item.quantity;
        });
        return total;
      }
  
      getUniqueCount(map = this.selection) {
        let count = 0;
        map.forEach((item) => {
          if (item.quantity > 0) count += 1;
        });
        return count;
      }
  
      _getSortedSelectionItems(map = this.selection) {
        const expanded = [];
  
        map.forEach((item) => {
          const qty = Math.max(0, Number(item.quantity) || 0);
          if (qty <= 0) return;
  
          const unitCount = item.isPreselected ? 1 : qty;
          for (let i = 0; i < unitCount; i++) {
            expanded.push({
              ...item,
              quantity: 1,
              _bundleUnitIndex: i,
              _bundleTotalQuantity: qty,
            });
          }
        });
  
        expanded.sort((a, b) => {
          if (a.isPreselected !== b.isPreselected) return a.isPreselected ? -1 : 1;
          return 0;
        });
  
        // Stamp slotIndex so discount/addon-property logic can target a single unit.
        expanded.forEach((item, index) => {
          item._bundleSlotIndex = index;
        });
  
        return expanded;
      }
  
      getConfiguredPreselectedCount() {
        return (this.config.preselectedProducts || []).length;
      }
  
      isDiscountAllProducts() {
        return this.config.discountApplication !== "milestone";
      }
  
      shouldShowDiscountProgress() {
        return Boolean(this.config.showDiscountProgress) && this.tiers.length > 0;
      }
  
      _getItemSlotIndex(item, map = this.selection) {
        const slotIndex = Number(item?._bundleSlotIndex);
        if (Number.isFinite(slotIndex) && slotIndex >= 0) return slotIndex;
  
        // Fallback for non-expanded objects (e.g. product cards / draft items):
        // return the first matching slot index, if any.
        const items = this._getSortedSelectionItems(map);
        return items.findIndex((entry) => String(entry.variantId) === String(item.variantId));
      }
  
      _getActiveDiscountTierForMap(map = this.selection) {
        return this._getActiveDiscountTier(this.getTotalQuantity(map));
      }
  
      _getDiscountPercentForItem(item, map = this.selection) {
        if (item.isPreselected) return 0;
  
        if (this.isDiscountAllProducts()) {
          const active = this._getActiveDiscountTierForMap(map);
          if (!active) return 0;
          return Number(active.percent) || 0;
        }
  
        const slotIndex = this._getItemSlotIndex(item, map);
        const tierForThisSlot = this._getTierForSlot(slotIndex);
        if (!tierForThisSlot) return 0;
  
        const totalQty = this.getTotalQuantity(map);
        if (totalQty < Number(tierForThisSlot.max)) return 0;
  
        return Number(tierForThisSlot.percent) || 0;
      }
  
      _getApplicableDiscountPercent(map = this.selection) {
        const active = this._getActiveDiscountTierForMap(map);
        return active ? Number(active.percent) || 0 : 0;
      }
  
      _itemQualifiesForAddonProperty(item, map = this.selection) {
        if (item.isPreselected) return false;
  
        const slotIndex = this._getItemSlotIndex(item, map);
        if (slotIndex < 0) return false;
  
        const tierForThisSlot = this._getTierForSlot(slotIndex);
        if (!tierForThisSlot) return false;
  
        const totalQty = this.getTotalQuantity(map);
        return totalQty >= Number(tierForThisSlot.max);
      }
  
      _getAddonPropertyForItem(item, map = this.selection) {
        if (!this._itemQualifiesForAddonProperty(item, map)) return null;
  
        const slotIndex = this._getItemSlotIndex(item, map);
        const tierForThisSlot = this._getTierForSlot(slotIndex);
        const name = tierForThisSlot?.addonPropertyName;
        return { name, value: "1" };
      }
  
      _getDiscountTierSlotIndex(tier) {
        return Number(tier.max) - 1;
      }
  
      _getTierForSlot(slotIndex) {
        return this.tiers.find((tier) => this._getDiscountTierSlotIndex(tier) === slotIndex) || null;
      }
  
      _getSlotDiscountBadgePercent(slotIndex) {
        const tier = this._getTierForSlot(slotIndex);
        if (!tier) return 0;
        return Number(tier.percent) || 0;
      }
  
      isSingleEmptySlotMode() {
        const value = this.config.showSingleEmptySlot;
        return value === true || value === "true";
      }
  
      _getVisibleSlotCount(filledCount) {
        const maxSlots = Number(this.config.maxProducts) || 1;
        if (!this.isSingleEmptySlotMode()) return maxSlots;
  
        return Math.min(filledCount + 1, maxSlots);
      }
  
      _getActiveDiscountTier(totalQty) {
        if (!this.tiers.length || totalQty <= 0) return null;
  
        let active = null;
        for (const tier of this.tiers) {
          if (totalQty >= Number(tier.max)) active = tier;
        }
        return active;
      }
  
      getDiscountPercent(totalQty) {
        const active = this._getActiveDiscountTier(totalQty);
        return active ? Number(active.percent) : 0;
      }
  
      getNextTier(totalQty) {
        if (!this.tiers.length) return null;
        for (const tier of this.tiers) {
          const max = Number(tier.max);
          if (totalQty < max) {
            return { remaining: max - totalQty, percent: Number(tier.percent) };
          }
        }
        return null;
      }
  
      getNextGiftTier(totalQty) {
        if (!this.freeGifts.length) return null;
        for (const gift of this.freeGifts) {
          if (!gift.available) continue;
          const min = Number(gift.minQuantity);
          if (totalQty < min) {
            return { remaining: min - totalQty, title: gift.title };
          }
        }
        return null;
      }
  
      getUnlockedGifts(totalQty) {
        const seen = new Set();
        const unlocked = [];
  
        for (const gift of this.freeGifts) {
          if (!gift.available) continue;
          if (totalQty < Number(gift.minQuantity)) continue;
  
          const variantId = String(gift.variantId);
          if (seen.has(variantId)) continue;
  
          seen.add(variantId);
          unlocked.push(gift);
        }
  
        return unlocked;
      }
  
      _getDiscountedUnitPrice(priceCents, item, map = this.selection) {
        const discount = item
          ? this._getDiscountPercentForItem(item, map)
          : this._getApplicableDiscountPercent(map);
        if (!discount) return priceCents;
        return Math.round((priceCents * (100 - discount)) / 100);
      }
  
      _getDiscountedAddonPrice(priceCents, item, map = this.selection) {
        return this._getDiscountedUnitPrice(priceCents, item, map);
      }
  
      _getDiscountedLineTotal(item, map = this.selection) {
        if (item.isPreselected) {
          return item.price * item.quantity;
        }
        const unitPrice = this._getDiscountedUnitPrice(item.price, item, map);
        return unitPrice * item.quantity;
      }
  
      _formatProductPrice(cents) {
        return formatMoney(cents, this.config.moneyFormat);
      }
  
      _setQuantity(variantId, quantity, isPopup) {
        const variantKey = String(variantId);
        const map = isPopup ? this.popupDraft : this.selection;
        const existingItem = map.get(variantKey);
  
        if (existingItem?.isPreselected && !this.canRemovePreselected) return;
        const product = this.productLookup.get(variantKey);
        if (!product || !product.available) return;
  
        quantity = Math.max(0, Math.min(quantity, this.config.maxQuantityPerProduct));
  
        const hasVariant = map.has(variantKey) && map.get(variantKey).quantity > 0;
  
        if (this.isPreselectedMode) {
          const currentCustomerQty = this.getCustomerSelectionQty(map);
          const existingQty = Math.max(0, Number(existingItem?.quantity) || 0);
          const nextCustomerQty = currentCustomerQty - (existingItem?.isPreselected ? 0 : existingQty) + quantity;
          if (quantity > 0 && nextCustomerQty > this.getMaxCustomerSlots()) return;
        } else {
          const currentTotalQty = this.getTotalQuantity(map);
          const existingQty = Math.max(0, Number(existingItem?.quantity) || 0);
          const nextTotalQty = currentTotalQty - existingQty + quantity;
          if (quantity > 0 && nextTotalQty > this.config.maxProducts) return;
        }
  
        if (quantity === 0) {
          map.delete(String(variantId));
        } else {
          map.set(String(variantId), { ...product, quantity });
        }
  
        if (isPopup) {
          this._updatePopupUI();
        } else {
          this._renderAll();
        }
      }
  
      _syncPopupFromSelection() {
        this.popupDraft = new Map();
        this.selection.forEach((item, key) => {
          this.popupDraft.set(key, { ...item });
        });
        this._updatePopupUI();
      }
  
      _confirmPopup() {
        this.selection = new Map();
        this.popupDraft.forEach((item, key) => {
          if (item.quantity > 0) this.selection.set(key, { ...item });
        });
        this._closePopup();
        this._renderAll();
      }
  
      _updatePopupUI() {
        this.popup?.querySelectorAll("[data-bundle-product-card]").forEach((card) => {
          const variantId = String(card.dataset.variantId);

          const qty = this.popupDraft.get(variantId)?.quantity || 0;
          const input = card.querySelector("[data-bundle-qty-input]");
          this._syncBundleQtyInput(input, qty);
          card.classList.toggle("is-selected", qty > 0);
  
          const product = this.productLookup.get(variantId);
          const draftItem = this.popupDraft.get(variantId);
          const item = draftItem || product;
          const itemDiscount = item ? this._getDiscountPercentForItem(item, this.popupDraft) : 0;
          const badge = card.querySelector("[data-bundle-card-discount-badge]");
          const priceEl = card.querySelector("[data-bundle-card-price]");
          const compareEl = card.querySelector("[data-bundle-card-compare]");
  
          if (badge) {
            badge.hidden = itemDiscount <= 0;
            if (itemDiscount > 0) badge.textContent = `-${itemDiscount}%`;
          }
  
          if (product && priceEl) {
            const discounted = this._getDiscountedAddonPrice(
              product.price,
              item || product,
              this.popupDraft
            );
            priceEl.textContent = this._formatProductPrice(discounted);
            if (compareEl) {
              const showCompare = itemDiscount > 0 && product.compareAtPrice > discounted;
              compareEl.hidden = !showCompare;
              if (showCompare) {
                compareEl.textContent = this._formatProductPrice(product.compareAtPrice || product.price);
              }
            }
          }
        });
  
        if (this.shouldShowDiscountProgress()) {
          this._updateDiscountTracker();
        }
      }
  
      _renderAll() {
        this._renderSelectionGrid();
        this._hydrateSlotRatings();
        if (this.shouldShowDiscountProgress()) {
          this._updateDiscountTracker();
          this._updateNextTierMessage();
        }
        this._updateFreeGiftsUI();
        this._updateDisplayedPrice();
      }
  
      _renderSelectionGrid() {
        if (!this.selectionGrid) return;
        const items = this._getSortedSelectionItems();
        const filledCount = items.length;
        const visibleSlots = this._getVisibleSlotCount(filledCount);
  
        this.selectionGrid.innerHTML = "";
  
        for (let i = 0; i < visibleSlots; i++) {
          const item = items[i];
          const isNext = i === filledCount;
          this.selectionGrid.appendChild(
            item ? this._createFilledSlot(item, i) : this._createEmptySlot(isNext, i)
          );
        }
      }
  
      _getPopupRatingSource(variantId) {
        const card = this.popup?.querySelector(
          `[data-bundle-product-card][data-variant-id="${String(variantId)}"]`
        );
        if (!card) return null;
        return (
          card.querySelector("a[href*='product-rating']") ||
          card.querySelector(".product-rating")?.closest("a")
        );
      }
  
      _isRatingRendered(snippet) {
        if (!snippet) return false;
        return Boolean(
          snippet.querySelector("i, .ruk-rating-snippet-count, svg") ||
          (snippet.childElementCount > 0 && snippet.textContent.trim())
        );
      }
  
      _fillSlotRating(variantId, container, attempt = 0) {
        if (!container) return;
  
        const existing = container.querySelector(".ruk_rating_snippet");
        if (this._isRatingRendered(existing)) return;
  
        const source = this._getPopupRatingSource(variantId);
        const snippet = source?.querySelector(".ruk_rating_snippet");
  
        if (source && this._isRatingRendered(snippet)) {
          container.replaceChildren(source.cloneNode(true));
          return;
        }
  
        if (attempt < 20) {
          clearTimeout(container._bundleRatingRetry);
          container._bundleRatingRetry = setTimeout(
            () => this._fillSlotRating(variantId, container, attempt + 1),
            250
          );
        }
      }
  
      _hydrateSlotRatings() {
        this.selectionGrid?.querySelectorAll("[data-bundle-slot-rating]").forEach((container) => {
          const variantId = container.closest(".bundle-slot--filled")?.dataset.variantId;
          if (variantId) this._fillSlotRating(variantId, container);
        });
      }
  
      _getDiscountTrackerRoots() {
        const roots = [...this.querySelectorAll("[data-bundle-discount]")];
  
        if (this.popup) {
          this.popup.querySelectorAll("[data-bundle-discount]").forEach((root) => {
            if (!roots.includes(root)) roots.push(root);
          });
        }
  
        return roots;
      }
  
      _updateDiscountTracker() {
        if (!this.shouldShowDiscountProgress()) return;
  
        this._getDiscountTrackerRoots().forEach((root) => {
          const isPopup = Boolean(this.popup?.contains(root));
          const map =
            isPopup && this.popup?.classList.contains("is-open") ? this.popupDraft : this.selection;
          const totalQty = this.getTotalQuantity(map);
  
          root.querySelectorAll("[data-bundle-discount-number]").forEach((el) => {
            const n = parseInt(el.dataset.bundleDiscountNumber, 10);
            el.classList.toggle("is-active", !Number.isNaN(n) && n > 0 && n <= totalQty);
          });
  
          const activeTier = this._getActiveDiscountTier(totalQty);
          const activeTierMax = activeTier ? Number(activeTier.max) : null;
  
          root.querySelectorAll("[data-bundle-discount-tier]").forEach((el) => {
            const max = parseInt(el.dataset.tierMax, 10);
            const unlocked = !Number.isNaN(max) && totalQty >= max;
            const isCurrent = unlocked && activeTierMax === max;
            el.classList.toggle("is-active", unlocked);
            el.classList.toggle("is-current", isCurrent);
          });
        });
      }
  
      _updateNextTierMessage() {
        if (!this.shouldShowDiscountProgress()) return;
  
        const el = this.querySelector("[data-bundle-discount-status]");
        if (!el) return;
  
        const template = this.config.texts?.discountNextTierText;
        const totalQty = this.getTotalQuantity(this.selection);
        const next = this.getNextTier(totalQty);
  
        if (!template || !next || next.remaining <= 0) {
          el.textContent = "";
          el.hidden = true;
          return;
        }
  
        const discountStr = `-${Number(next.percent).toFixed(2)}%`;
        const countStr = String(next.remaining);
        el.innerHTML = template
          .replace("[count]", `<strong>${countStr}</strong>`)
          .replace("[discount]", `<strong>${discountStr}</strong>`);
        el.hidden = false;
      }
  
      _updateGiftLineFill(container, items, unlockedCount) {
        const lineFill = container.querySelector("[data-bundle-gifts-line-fill]");
        const line = container.querySelector(".bundle-gifts__line");
        if (!lineFill || !line) return;
  
        if (unlockedCount <= 0) {
          lineFill.style.height = "0px";
          return;
        }
  
        let lastUnlockedIndex = -1;
        items.forEach((item, index) => {
          if (item.classList.contains("is-unlocked")) lastUnlockedIndex = index;
        });
  
        const lastUnlockedItem = items[lastUnlockedIndex];
        const marker = lastUnlockedItem?.querySelector("[data-bundle-gift-marker]");
        if (!marker) {
          lineFill.style.height = "0px";
          return;
        }
  
        const updateHeight = () => {
          const lineRect = line.getBoundingClientRect();
          const markerRect = marker.getBoundingClientRect();
          const markerCenterY = markerRect.top + markerRect.height / 2 - lineRect.top;
          lineFill.style.height = `${Math.max(0, Math.round(markerCenterY))}px`;
        };
  
        updateHeight();
        requestAnimationFrame(updateHeight);
      }
  
      _updateFreeGiftsUI() {
        const container = this.querySelector("[data-bundle-gifts]");
        if (!container) return;
  
        const list = container.querySelector(".bundle-gifts__list");
        const items = [...container.querySelectorAll("[data-bundle-gift]")];
  
        if (list) {
          items
            .sort(
              (a, b) =>
                (parseInt(a.dataset.minQuantity, 10) || 0) - (parseInt(b.dataset.minQuantity, 10) || 0)
            )
            .forEach((item) => list.appendChild(item));
        }
        const totalQty = this.getTotalQuantity(this.selection);
        let unlockedCount = 0;
  
        items.forEach((item) => {
          const min = parseInt(item.dataset.minQuantity, 10) || 1;
          const unlocked = totalQty >= min;
          item.classList.toggle("is-unlocked", unlocked);
          if (unlocked) unlockedCount += 1;
        });
  
        this._updateGiftLineFill(container, items, unlockedCount);
  
        const statusEl = container.querySelector("[data-bundle-gifts-status]");
        if (!statusEl) return;
  
        const template = this.config.texts?.giftsNextTierText;
        const next = this.getNextGiftTier(totalQty);
  
        if (!template || !next || next.remaining <= 0) {
          statusEl.textContent = "";
          statusEl.hidden = true;
          return;
        }
  
        statusEl.innerHTML = template
          .replace("[count]", `<strong>${next.remaining}</strong>`)
          .replace("[gift]", `<strong>${next.title}</strong>`);
        statusEl.hidden = false;
      }
  
      _formatDiscountBadgePercent(percent) {
        const value = Number(percent) || 0;
        if (value <= 0) return "";
        const formatted = Number.isInteger(value) ? String(value) : value.toFixed(2);
        return `-${formatted}%`;
      }
  
      _formatSlotUnitPrice(item) {
        const measurement = item.unitPriceMeasurement;
        const unitPrice = Number(item.unitPrice);
        if (!measurement || unitPrice <= 0) return "";
  
        const refValue = measurement.referenceValue;
        const refUnit = measurement.referenceUnit;
        return `${this._formatProductPrice(unitPrice)} / ${refValue}${refUnit}`;
      }
  
      _getFilledSlotPricesMarkup(item) {
        const showPrices = Boolean(this.config.showSlotPrices);
        const showUnitPrice = Boolean(this.config.showSlotUnitPrice);
        if (!showPrices && !showUnitPrice) return "";
  
        const salePrice = item.isPreselected
          ? item.price
          : this._getDiscountedUnitPrice(item.price, item, this.selection);
        const compareAt = Number(item.compareAtPrice) || 0;
        const showCompare = showPrices && compareAt > salePrice;
        const unitPriceLine = showUnitPrice ? this._formatSlotUnitPrice(item) : "";
  
        if (!showCompare && !showPrices && !unitPriceLine) return "";
        if (showPrices && !showCompare && !unitPriceLine && salePrice <= 0) return "";
  
        return `
          <div class="bundle-slot__prices">
            ${
              showCompare
                ? `<p class="bundle-slot__price-compare">${this._formatProductPrice(compareAt)}</p>`
                : ""
            }
            ${showPrices ? `<p class="bundle-slot__price">${this._formatProductPrice(salePrice)}</p>` : ""}
            ${unitPriceLine ? `<p class="bundle-slot__unit-price">${unitPriceLine}</p>` : ""}
          </div>
        `;
      }
  
      _createEmptySlot(isNext, slotIndex) {
        const discountPercent = this._getSlotDiscountBadgePercent(slotIndex);
        const badgeLabel = discountPercent > 0 ? this._formatDiscountBadgePercent(discountPercent) : "";
        const slot = document.createElement("button");
        slot.type = "button";
        slot.className = `bundle-slot bundle-slot--empty${isNext ? " is-next" : ""}`;
        slot.innerHTML = `
          ${badgeLabel ? `<span class="bundle-slot__badge">${badgeLabel}</span>` : ""}
          <span class="bundle-slot__plus" aria-hidden="true">
           <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
           <path d="M2.5 8H13.5" stroke="#ED8A00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
           <path d="M8 2.5V13.5" stroke="#ED8A00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          </span>
          <span class="bundle-slot__label">${this.config.texts.addProductLabel}</span>
        `;
        slot.addEventListener("click", () => this._openPopup());
        return slot;
      }
  
      _createFilledSlot(item, slotIndex) {
        const isPreselected = item.isPreselected;
        const showRemove = !isPreselected || this.canRemovePreselected;
        const discountPercent = isPreselected
          ? 0
          : this._getDiscountPercentForItem(item, this.selection);
        const badgeLabel = discountPercent > 0 ? this._formatDiscountBadgePercent(discountPercent) : "";
        const showQtyBadge = true;
        const slot = document.createElement("div");
        slot.className = `bundle-slot bundle-slot--filled${isPreselected ? " bundle-slot--preselected" : ""}`;
        slot.dataset.variantId = item.variantId;
        slot.innerHTML = `
          <div class="bundle-slot__media-wrap">
            ${badgeLabel ? `<span class="bundle-slot__badge">${badgeLabel}</span>` : ""}
            ${item.image ? `<img class="bundle-slot__image" src="${item.image}" alt="" loading="lazy">` : ""}
            ${
              showRemove
                ? `<button type="button" class="bundle-slot__remove" data-bundle-remove aria-label="Remove"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"> <path d="M9.375 2.625L2.625 9.375" stroke="#D8C2AD" stroke-linecap="round" stroke-linejoin="round"/> <path d="M9.375 9.375L2.625 2.625" stroke="#D8C2AD" stroke-linecap="round" stroke-linejoin="round"/> </svg></button>`
                : ""
            }
            ${
              showQtyBadge
                ? `<span class="bundle-slot__qty-badge" aria-label="Quantity">${item.quantity}</span>`
                : ""
            }
          </div>
          <p class="bundle-slot__category">${item.category || ""}</p>
          <p class="bundle-slot__title">${item.title}</p>
          ${this._getFilledSlotPricesMarkup(item)}
          <div class="bundle-slot__rating" data-bundle-slot-rating></div>
        `;
  
        this._fillSlotRating(item.variantId, slot.querySelector("[data-bundle-slot-rating]"));
  
        if (showRemove) {
          slot.querySelector("[data-bundle-remove]")?.addEventListener("click", (e) => {
            e.stopPropagation();
            const variantId = String(item.variantId);
            const existing = this.selection.get(variantId);
            const currentQty = Math.max(0, Number(existing?.quantity) || 0);
            const nextQty = currentQty - 1;
            if (nextQty <= 0) {
              this.selection.delete(variantId);
            } else {
              this.selection.set(variantId, { ...existing, quantity: nextQty });
            }
            this._renderAll();
          });
        }
  
        return slot;
      }
  
      _readMainVariantPrice() {
        const idInput = this.form?.querySelector('[name="id"]');
        if (idInput?.value) this._fetchVariantPrice(idInput.value);
      }
  
      _onVariantChange(event) {
        const variant = event.detail?.variant;
        if (variant) {
          this.mainVariantPrice = variant.price;
          this._scheduleDisplayedPriceUpdate();
        }
      }
  
      _scheduleDisplayedPriceUpdate() {
        clearTimeout(this._displayPriceUpdateTimer);
        this._displayPriceUpdateTimer = setTimeout(() => this._updateDisplayedPrice(), 0);
      }
  
      async _fetchVariantPrice(variantId) {
        try {
          const res = await fetch(`${Shopify.routes.root}variants/${variantId}.js`);
          if (res.ok) {
            const variant = await res.json();
            this.mainVariantPrice = variant.price;
            this._scheduleDisplayedPriceUpdate();
          }
        } catch { }
      }
  
      _getMainQuantity() {
        const qtyInput = this.form?.querySelector('[name="quantity"]');
        return qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      }
  
      _getMainLineTotal() {
        if (this.mainVariantPrice == null) return 0;
        return this.mainVariantPrice * this._getMainQuantity();
      }
  
      _getAddonsSubtotal(map = this.selection, applyDiscount = false) {
        if (!applyDiscount) {
          let subtotal = 0;
          map.forEach((item) => {
            if (item.quantity <= 0) return;
            subtotal += item.price * item.quantity;
          });
          return subtotal;
        }
  
        const slots = this._getSortedSelectionItems(map);
        return slots.reduce((sum, slotItem) => {
          if (slotItem.isPreselected) return sum + slotItem.price; // qty is always 1 in slots
          return sum + this._getDiscountedUnitPrice(slotItem.price, slotItem, map);
        }, 0);
      }
  
      _setSalePriceText(salePrice, formatted) {
        for (const node of salePrice.childNodes) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            node.textContent = formatted;
            return;
          }
        }
  
        const textSpan = document.createElement("span");
        textSpan.textContent = formatted;
        salePrice.appendChild(textSpan);
      }
  
      _updateDisplayedPrice() {
        const mainTotal = this._getMainLineTotal();
        const addonQty = this.getTotalQuantity(this.selection);
        const displayTotal =
          addonQty === 0
            ? mainTotal
            : mainTotal + this._getAddonsSubtotal(this.selection, true);

        const salePrices = document.querySelectorAll(`sale-price[form="${this.config.formId}"]`);
        if (salePrices.length) {
          const formatted = formatMoney(displayTotal, this.config.moneyFormat);

          salePrices.forEach((salePrice) => {
            this._setSalePriceText(salePrice, formatted);
            salePrice.setAttribute("data-bundle-price-updated", "");
          });
        }

        this.dispatchEvent(
          new CustomEvent("bundle:price-change", {
            bubbles: true,
            detail: { price: displayTotal },
          })
        );
      }
  
      async _onFormSubmit(event) {
        const bundleQty = this.getTotalQuantity(this.selection);
        const hasUnlockedGifts = this.getUnlockedGifts(bundleQty).length > 0;
        if (bundleQty === 0 && !hasUnlockedGifts) return;
  
        event.preventDefault();
        event.stopImmediatePropagation();
  
        const submitButtons = Array.from(this.form.elements).filter((el) => el.type === "submit");
        submitButtons.forEach((btn) => {
          btn.setAttribute("disabled", "disabled");
          btn.setAttribute("aria-busy", "true");
        });
  
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
  
        const labels = getBundlePropertyLabels(this.config);
        const timeStamp = Date.now();
        const timeStampValue = String(timeStamp);
        const mainVariantId = String(this.form.querySelector('[name="id"]')?.value);
        const parentQuantity = this._getMainQuantity();
  
        const items = [
          {
            id: mainVariantId,
            quantity: parentQuantity,
            properties: {
              [labels.main]: timeStampValue,
            },
          },
        ];
  
        const slotItems = this._getSortedSelectionItems(this.selection);
        slotItems.forEach((slotItem) => {
          const variantId = String(slotItem.variantId);
  
          const properties = {
            [labels.addons]: timeStampValue,
          };
  
          const addonProperty = this._getAddonPropertyForItem(slotItem, this.selection);
          if (addonProperty) {
            properties[addonProperty.name] = addonProperty.value;
          }
  
          items.push({
            id: variantId,
            quantity: 1,
            parent_id: mainVariantId,
            properties,
          });
        });
  
        const addedGiftVariantIds = new Set();
        this.getUnlockedGifts(bundleQty).forEach((gift) => {
          const variantId = String(gift.variantId);
          if (addedGiftVariantIds.has(variantId)) return;
          addedGiftVariantIds.add(variantId);
  
          items.push({
            id: variantId,
            quantity: parentQuantity,
            parent_id: mainVariantId,
            properties: {
              [labels.addons]: timeStampValue,
              _bundle_gift: "1",
            },
          });
        });
  
        try {
          const sectionsToBundle = prepareBundledSections();
          const onSuccessDo = new FormData(this.form).get("on_success");
  
          const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              items,
              sections: sectionsToBundle.join(","),
              sections_url: `${Shopify.routes.root}variants/${mainVariantId}`,
            }),
          });
  
          const responseJson = await response.json();
  
          document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
          submitButtons.forEach((btn) => {
            btn.removeAttribute("disabled");
            btn.removeAttribute("aria-busy");
          });
  
          if (response.ok) {
            BundleCartSync.cartCache = null;
  
            if (
              window.themeVariables?.settings?.cartType === "page" ||
              window.themeVariables?.settings?.pageType === "cart"
            ) {
              window.location.href = `${Shopify.routes.root}cart`;
              return;
            }
  
            const cartContent = await BundleCartSync.fetchCart(true);
            cartContent.sections = responseJson.sections || {};
  
            const eventDetail = {
              items: responseJson.items || [responseJson],
              cart: cartContent,
              onSuccessDo,
            };
  
            this.form.dispatchEvent(
              new CustomEvent("variant:add", {
                bubbles: true,
                detail: eventDetail,
              })
            );
  
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:change", {
                bubbles: true,
                detail: {
                  baseEvent: "variant:add",
                  cart: cartContent,
                  onSuccessDo,
                },
              })
            );
          } else {
            this.form.dispatchEvent(
              new CustomEvent("cart:error", {
                bubbles: true,
                detail: { error: responseJson.description || responseJson.message },
              })
            );
          }
        } catch (error) {
          document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
          submitButtons.forEach((btn) => {
            btn.removeAttribute("disabled");
            btn.removeAttribute("aria-busy");
          });
          this.form.dispatchEvent(
            new CustomEvent("cart:error", { bubbles: true, detail: { error: error.message } })
          );
        }
      }
    }
  
    function init() {
      BundleCartSync.init();
  
      if (!customElements.get("bundle-builder")) {
        customElements.define("bundle-builder", BundleBuilder);
      }
    }
  
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
    } else {
      init();
    }
  })();
  